import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, InlineAlert, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { getUSState, readableDistanceFromStore } from 'features/store/utils';
import { useIntl } from 'react-intl';

import { useAppTakeoverCart } from 'components/app-takeover/use-app-takeover-cart';
import { CardModal } from 'components/card-modal';
import { PaymentProcessLoading } from 'components/payment-process-loading';
import { StoreSanityOrderEta } from 'components/sanity-store-order-eta/store-sanity-order-eta';
import StaticMap from 'components/static-map';
import { useOrderPayment } from 'experiments/checkout-v2/control/pages/cart/payment/order-payment/use-order-payment';
import { logCartPlaceOrder } from 'experiments/checkout-v2/control/pages/cart/payment/utils';
import { useDistance } from 'hooks/geolocation/use-distance';
import { useCart } from 'hooks/use-cart';
import { usePrevious } from 'hooks/use-previous';
import { PlaceOrderButton } from 'pages/cart/place-order-button';
import { CustomEventNames, EventTypes, logNavigationClick, logRBIEvent } from 'state/amplitude';
import { useGeolocation } from 'state/geolocation';
import { actions, useAppDispatch } from 'state/global-state';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { primitive } from 'styles/constants/primitives';
import { isWeb } from 'utils/environment';
import { routes } from 'utils/routing';
import { getHeadingForServiceMode } from 'utils/service-mode';

import {
  Address,
  LocationContainer,
  ServiceModeContainer,
  ServiceModeHeading,
  ServiceModeIcon,
  StreetAddress,
  StyledLink,
  StyledPicture,
} from './styled';

const StoreConfirmationModal: React.FC = () => {
  const { region } = useLocale();
  const router = useRouter();
  const { store } = useStoreContext();
  const { shouldConfirmStoreOnCheckout } = useUIContext();
  const { physicalAddress, restaurantImage } = store;
  const dispatch = useAppDispatch();
  const { hasPaymentProcessingTakeover } = useAppTakeoverCart();
  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);
  const isMobileOrderForDriveThruLaneEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  );
  const streetAddress = physicalAddress?.address1 ?? '';
  const formattedCity = physicalAddress?.city ?? '';
  const state = getUSState(physicalAddress?.stateProvince ?? '');
  const postalCode = (physicalAddress?.postalCode ?? '').split('-')[0];
  const { formatMessage } = useIntl();
  const { serviceMode } = useServiceModeContext();
  const userAwayEventSent = useRef<boolean>(false);
  const { cartEntries } = useCart();
  const [isMounted, setIsMounted] = useState(false);
  const maxDistanceUserAway = useFlag(LaunchDarklyFlag.USER_AWAY_FROM_STORE);
  const { isPermissionGranted } = useGeolocation();
  const distance = useDistance({ restaurant: store, shouldUseUserCoordinatesIfAvailable: true });
  const distanceText = distance ? readableDistanceFromStore(distance, region, formatMessage) : '';
  const userIsAwayFromStore =
    isPermissionGranted && maxDistanceUserAway > 0 && distance && distance > maxDistanceUserAway;
  const serviceModeHeadingConfig = getHeadingForServiceMode(
    formatMessage,
    isFastLaneEnabled,
    isMobileOrderForDriveThruLaneEnabled,
    serviceMode
  );

  const {
    commitOrder,
    isProcessingPayment,
    isErrorModalOpen,
    ErrorDialog,
    isPlacingOrder,
    isLoadingPayments,
  } = useOrderPayment();

  const prevIsErrorModalOpen = usePrevious(isErrorModalOpen);

  useEffect(() => {
    if (prevIsErrorModalOpen && !isErrorModalOpen) {
      router.back();
    }
  }, [isErrorModalOpen, prevIsErrorModalOpen, router]);

  const chooseDifferentLocation = useCallback(() => {
    if (isProcessingPayment) {
      return;
    }
    logNavigationClick(CustomEventNames.BUTTON_CLICK_OTHER_LOCATION);
    shouldConfirmStoreOnCheckout(false);
    router.navigate(routes.cart);
    router.navigate({
      pathname: routes.storeLocator,
    });
  }, [isProcessingPayment, shouldConfirmStoreOnCheckout, router]);

  const handleConfirm = useCallback(() => {
    if (isProcessingPayment) {
      return;
    }

    logCartPlaceOrder();
    // Set shouldRefetchOffers to true on order
    dispatch(actions.loyalty.setShouldRefetchOffers(true));
    commitOrder();
  }, [commitOrder, dispatch, isProcessingPayment]);

  // You should never be on this page if you have an empty cart.
  // Back button can do this! So lets just redirect to home if it happens.
  // hadCartEntries prevents us from redirecting off of confirm page right when cart empties on order (race condition on browser)
  const hadCartEntriesRef = useRef(!!cartEntries.length);
  let hadCartEntries = false;
  if (cartEntries.length || hadCartEntriesRef.current) {
    hadCartEntries = true;
    hadCartEntriesRef.current = true;
  }
  useEffect(() => {
    // to allow this to run in _layout files which run before normal react code and causes errors without this.
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (userIsAwayFromStore && !userAwayEventSent.current) {
      logRBIEvent({
        name: CustomEventNames.MODAL_APPEARANCE,
        type: EventTypes.Other,
        attributes: {
          name: 'Away from store alert',
        },
      });
      userAwayEventSent.current = true;
    }
  }, [userIsAwayFromStore]);

  useEffect(() => {
    if (!isMounted) {
      return;
    }
    if (!hadCartEntries) {
      router.replace(routes.base);
    }
  }, [router, hadCartEntries, isMounted]);

  return (
    <CardModal
      headerText={formatMessage({ id: 'confirmYourStore' })}
      testID="store-confirmation-modal"
      analyticsEvent="Confirm store"
      dismissType="back"
      footerBackgroundColor={primitive.bk.$mayoEggWhite}
      wrappedChildrenStyle={isWeb ? { height: null } : null}
      invertHeaderColor
      webAnimate={false}
      isFullScreen
      footer={
        <>
          <LocationContainer>
            <ServiceModeContainer>
              <ServiceModeIcon variant={serviceModeHeadingConfig?.icon} />
              <ServiceModeHeading>{serviceModeHeadingConfig?.heading}</ServiceModeHeading>
            </ServiceModeContainer>
            <StreetAddress nativeID="street-address" focusable>
              {streetAddress}
            </StreetAddress>
            <Address nativeID="city-state-zip-address" focusable>
              <Text fontSize={'sm'}>
                {formattedCity} {state} {postalCode}
              </Text>
              <Text fontWeight={'bold'}>{distanceText}</Text>
            </Address>
            <Box width={'142px'} pt={'$3'}>
              <StoreSanityOrderEta store={store} />
            </Box>
          </LocationContainer>
          {!!userIsAwayFromStore && (
            <InlineAlert
              alertContainerStyles={{
                alignItems: 'center',
              }}
              titleStyles={{
                color: primitive.bk.$fieryRed,
              }}
              iconStyles={{
                color: primitive.bk.$fieryRed,
              }}
              width="full"
              status="error"
              title={formatMessage({ id: 'userAwayFromStore' })}
              w="full"
              mb="$4"
            />
          )}
          <PlaceOrderButton
            confirmation
            onPress={handleConfirm}
            isLoading={isProcessingPayment}
            isDisabled={isProcessingPayment}
          />
          <StyledLink onPress={chooseDifferentLocation}>
            {formatMessage({ id: 'confirmationDifferentLocation' })}
          </StyledLink>
        </>
      }
    >
      {restaurantImage ? (
        <StyledPicture image={restaurantImage} alt={store.name ?? 'Restaurant Name Missing'} />
      ) : (
        <StaticMap
          initialZoomIOS={8000}
          position={{
            lat: store.latitude ?? 0,
            lng: store.longitude ?? 0,
          }}
          title={store.name || store.physicalAddress?.address1 || undefined}
        />
      )}
      <ErrorDialog />
      {hasPaymentProcessingTakeover &&
        !isErrorModalOpen &&
        isPlacingOrder &&
        !isLoadingPayments && <PaymentProcessLoading />}
    </CardModal>
  );
};

// eslint-disable-next-line import/no-default-export
export default StoreConfirmationModal;
